/**
 * useFactFindImport — state machine for the fact-find document import flow:
 *   idle → uploading → extracting (poll) → review → committing → done | error
 *
 * Owns upload, job polling, the editable/selectable working set, and commit.
 * Components consume this hook and never touch the service layer directly.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  startFactFindImport,
  getFactFindImportJob,
  commitFactFindImport,
  FactFindImportJob,
  FactFindImportSection,
  CommitImportResult,
} from '../../services/factfindImport.service';

export type ImportPhase =
  | 'idle'
  | 'uploading'
  | 'extracting'
  | 'review'
  | 'committing'
  | 'done'
  | 'error';

export interface WorkingEntity {
  key: string;
  extraction: Record<string, any>;
  className: string;
  label: string;
  instanceId: string;
  isNew: boolean;
  mergesInto: { instanceId: string; properties: Record<string, unknown> } | null;
  include: boolean;
}

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 180; // ~6 minutes

function buildWorkingEntities(job: FactFindImportJob): WorkingEntity[] {
  const preview: FactFindImportSection[] = job.preview || [];
  // index preview entities by class::instanceId for merge metadata + labels
  const meta = new Map<
    string,
    { label: string; isNew: boolean; mergesInto: any }
  >();
  preview.forEach((section) => {
    (section.entities || []).forEach((e) => {
      meta.set(`${e.class}::${e.instanceId}`, {
        label: section.label || section.class,
        isNew: e.isNew,
        mergesInto: e.mergesInto,
      });
    });
  });

  return (job.extractions || []).map((ex: any) => {
    const className = String(ex.class ?? '');
    const instanceId = String(ex.instance_id ?? '');
    const key = `${className}::${instanceId}`;
    const m = meta.get(key);
    return {
      key,
      extraction: ex,
      className,
      instanceId,
      label: m?.label || className,
      isNew: m ? m.isNew : true,
      mergesInto: m?.mergesInto ?? null,
      include: true,
    };
  });
}

export function useFactFindImport(clientId: string) {
  const [phase, setPhase] = useState<ImportPhase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [job, setJob] = useState<FactFindImportJob | null>(null);
  const [entities, setEntities] = useState<WorkingEntity[]>([]);
  const [result, setResult] = useState<CommitImportResult | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const mounted = useRef(true);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, []);

  const reset = useCallback(() => {
    if (pollTimer.current) clearTimeout(pollTimer.current);
    setPhase('idle');
    setError(null);
    setFileName('');
    setJob(null);
    setEntities([]);
    setResult(null);
    setNotice(null);
  }, []);

  const poll = useCallback((jobId: string, attempt: number) => {
    getFactFindImportJob(jobId)
      .then((j) => {
        if (!mounted.current) return;
        if (j.status === 'complete') {
          setJob(j);
          setEntities(buildWorkingEntities(j));
          setPhase('review');
        } else if (j.status === 'failed') {
          setError(j.error || j.message || 'Extraction failed.');
          setPhase('error');
        } else if (attempt >= MAX_POLLS) {
          setError('Extraction timed out. Please try again.');
          setPhase('error');
        } else {
          pollTimer.current = setTimeout(() => poll(jobId, attempt + 1), POLL_INTERVAL_MS);
        }
      })
      .catch((e) => {
        if (!mounted.current) return;
        setError(e?.message || 'Failed to check extraction status.');
        setPhase('error');
      });
  }, []);

  const start = useCallback(
    async (file: File) => {
      setError(null);
      setResult(null);
      setFileName(file.name);
      setPhase('uploading');
      try {
        const res = await startFactFindImport(clientId, file);
        if (!mounted.current) return;
        // Identical file already imported → nothing to parse/review.
        if (res.alreadyImported || !res.jobId) {
          const when = res.importedAt
            ? ` on ${new Date(res.importedAt).toLocaleDateString()}`
            : '';
          setNotice(
            res.alreadyImported
              ? `"${file.name}" has already been imported${when}. Nothing new to import.`
              : 'This file has already been imported.',
          );
          setPhase('done');
          return;
        }
        setPhase('extracting');
        poll(res.jobId, 0);
      } catch (e: any) {
        if (!mounted.current) return;
        setError(e?.message || 'Upload failed.');
        setPhase('error');
      }
    },
    [clientId, poll],
  );

  const toggleEntity = useCallback((key: string) => {
    setEntities((prev) =>
      prev.map((e) => (e.key === key ? { ...e, include: !e.include } : e)),
    );
  }, []);

  const updateProp = useCallback((key: string, prop: string, value: string) => {
    setEntities((prev) =>
      prev.map((e) =>
        e.key === key
          ? {
              ...e,
              extraction: {
                ...e.extraction,
                extracted_properties: {
                  ...(e.extraction.extracted_properties || {}),
                  [prop]: value,
                },
              },
            }
          : e,
      ),
    );
  }, []);

  const commit = useCallback(async () => {
    if (!job) return;
    const included = entities.filter((e) => e.include).map((e) => e.extraction);
    setPhase('committing');
    setError(null);
    try {
      const res = await commitFactFindImport(clientId, {
        meetingId: job.meeting_id || '',
        transcriptionId: job.transcription_id || '',
        extractions: included,
      });
      if (!mounted.current) return;
      setResult(res);
      setPhase('done');
    } catch (e: any) {
      if (!mounted.current) return;
      setError(e?.message || 'Failed to import into the graph.');
      setPhase('error');
    }
  }, [clientId, job, entities]);

  const includedCount = entities.filter((e) => e.include).length;

  return {
    phase,
    error,
    fileName,
    entities,
    includedCount,
    result,
    notice,
    start,
    toggleEntity,
    updateProp,
    commit,
    reset,
  };
}
